import type {
  ResearchSweep,
  ResearchSweepAction,
  ResearchSweepStatus,
  ResearchUnit,
  ResearchUnitKind,
  ResearchUnitKindCounts,
  ResearchUnitStatus,
  WebResearchSettings,
} from "@backchannel/tax-contracts";
import { ufSchema } from "@backchannel/tax-contracts";

/**
 * Presentation helpers for Administração → Pesquisa web. Pure functions only:
 * the page reads sweeps, units and settings from the admin API and derives
 * stage progress, ETA and the document list here.
 */

export type SweepStageId = "locate" | "fetch" | "extract" | "verify";

export const SWEEP_STAGES: ReadonlyArray<{ id: SweepStageId; kinds: ResearchUnitKind[] }> = [
  {
    id: "locate",
    kinds: [
      "LOCATE_APPLIANCE_PRODUCT",
      "LOCATE_SEGMENT_UF",
      "LOCATE_CONFAZ_SEGMENT",
      "LOCATE_INTERNAL_RATE_UF",
      "LOCATE_INTERSTATE_TABLE",
    ],
  },
  { id: "fetch", kinds: ["FETCH_DOCUMENT"] },
  { id: "extract", kinds: ["EXTRACT_DOCUMENT_SEGMENT"] },
  { id: "verify", kinds: ["VERIFY_PAIR"] },
];

/** Status groups counted per stage: finished (done + cached) and failed. */
export const STAGE_FINISHED: ResearchUnitStatus[] = ["DONE", "CACHED"];
export const STAGE_FAILED: ResearchUnitStatus[] = ["FAILED"];

export function stageCountGroups() {
  return SWEEP_STAGES.flatMap((stage) => [
    { key: `${stage.id}:finished`, kind: stage.kinds, status: STAGE_FINISHED },
    { key: `${stage.id}:failed`, kind: stage.kinds, status: STAGE_FAILED },
  ]);
}

export function stageTotal(byKind: ResearchUnitKindCounts, kinds: ResearchUnitKind[]): number {
  return kinds.reduce((sum, kind) => sum + (byKind[kind] ?? 0), 0);
}

export const SWEEP_STATUS_PRESENTATION: Record<
  ResearchSweepStatus | "COMPLETED_WITH_FAILURES",
  { glyph: string; tone: "positive" | "neutral" | "info" | "caution" | "critical" }
> = {
  QUEUED: { glyph: "◔", tone: "neutral" },
  RUNNING: { glyph: "●", tone: "info" },
  PAUSED: { glyph: "‖", tone: "caution" },
  COMPLETED: { glyph: "✓", tone: "positive" },
  COMPLETED_WITH_FAILURES: { glyph: "✓", tone: "caution" },
  CANCELLED: { glyph: "■", tone: "neutral" },
  BUDGET_EXHAUSTED: { glyph: "!", tone: "critical" },
};

export function isLiveSweep(status: ResearchSweepStatus): boolean {
  return status === "QUEUED" || status === "RUNNING";
}

type Action = ResearchSweepAction["action"];

/** Header actions the API accepts in each status (mirrors `actOnSweep`). */
export function sweepActions(sweep: ResearchSweep): Action[] {
  const actions: Action[] = [];
  if (sweep.status === "QUEUED" || sweep.status === "RUNNING") actions.push("PAUSE");
  if (sweep.status === "PAUSED") actions.push("RESUME");
  if (sweep.status !== "COMPLETED" && sweep.status !== "CANCELLED") actions.push("CANCEL");
  return actions;
}

function liveOrPaused(sweep: ResearchSweep): boolean {
  return isLiveSweep(sweep.status) || sweep.status === "PAUSED";
}

/**
 * Nothing left to claim: every open unit waits for a document download
 * (FETCH → ingestion). The API fails those units on its own once the
 * download failed or timed out; until then the operator may retry or close.
 */
export function isStalledSweep(sweep: ResearchSweep): boolean {
  return (
    liveOrPaused(sweep) &&
    sweep.totals.blocked > 0 &&
    sweep.totals.pending === 0 &&
    sweep.totals.inProgress === 0
  );
}

/** CLOSE needs a live or paused sweep with no unit in progress. */
export function canCloseSweep(sweep: ResearchSweep): boolean {
  return liveOrPaused(sweep) && sweep.totals.inProgress === 0;
}

export function canRetryStalled(sweep: ResearchSweep): boolean {
  return liveOrPaused(sweep) && sweep.totals.blocked > 0;
}

/** Status label key: a completed sweep with failed units reads "completed with failures". */
export function sweepStatusKey(
  sweep: Pick<ResearchSweep, "status" | "completion">,
): ResearchSweepStatus | "COMPLETED_WITH_FAILURES" {
  return sweep.status === "COMPLETED" && sweep.completion?.outcome === "COMPLETED_WITH_FAILURES"
    ? "COMPLETED_WITH_FAILURES"
    : sweep.status;
}

export function canRetryFailed(sweep: ResearchSweep): boolean {
  return sweep.status !== "CANCELLED" && sweep.totals.failed > 0;
}

/** Decimal US$ strings from the API as numbers for display and ratios only. */
export function usd(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function ratio(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, value / total));
}

/**
 * Minutes left at the configured paces. Grounded and extraction calls run in
 * parallel lanes, so the slower lane sets the ETA. Null when nothing will run.
 */
export function sweepEtaMinutes(
  sweep: ResearchSweep,
  settings: Pick<WebResearchSettings, "groundedCallsPerMinute" | "extractionCallsPerMinute">,
): number | null {
  // A stalled sweep waits for downloads, not for the paces.
  if (!isLiveSweep(sweep.status) || isStalledSweep(sweep)) return null;
  const groundedLeft = Math.max(
    0,
    Math.min(sweep.estimate.groundedCalls, sweep.maxGroundedCalls) - sweep.groundedCalls,
  );
  const extractionLeft = Math.max(0, sweep.estimate.extractionCalls - sweep.extractionCalls);
  const minutes = Math.max(
    groundedLeft / Math.max(1, settings.groundedCallsPerMinute),
    extractionLeft / Math.max(1, settings.extractionCallsPerMinute),
  );
  return Math.ceil(minutes);
}

/**
 * ETA of a live sweep: at the real rate the API measured over the last
 * minutes (sweep detail `throughput`) when units settled in that window,
 * otherwise from the configured paces. Null when nothing is left to wait for.
 */
export function liveSweepEta(
  sweep: ResearchSweep,
  settings:
    | Pick<WebResearchSettings, "groundedCallsPerMinute" | "extractionCallsPerMinute">
    | undefined,
): { minutes: number; basis: "RATE" | "PACE" } | null {
  if (!isLiveSweep(sweep.status) || isStalledSweep(sweep)) return null;
  const measured = sweep.throughput?.etaSeconds;
  if (measured !== null && measured !== undefined) {
    return { minutes: Math.ceil(measured / 60), basis: "RATE" };
  }
  const paced = settings ? sweepEtaMinutes(sweep, settings) : null;
  return paced === null ? null : { minutes: paced, basis: "PACE" };
}

/** "3 h 20 min", "45 min", "< 1 min". Units stay language-neutral. */
export function formatMinutes(minutes: number): string {
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (hours >= 48) return `${Math.round(hours / 24)} d`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export type DocumentChange = "NEW" | "CHANGED" | "SAME" | "PENDING";

export type SweepDocument = {
  url: string;
  host: string;
  uf: string | null;
  segments: string[];
  sha: string | null;
  change: DocumentChange;
  failed: boolean;
};

function documentUrl(unit: ResearchUnit): string | null {
  return unit.key.canonicalUrl ?? null;
}

/**
 * Official documents a sweep located, from its FETCH and EXTRACT units (the
 * admin API has no document list yet). "Changed" compares the content hash
 * with the previous sweep that saw the same URL.
 */
export function sweepDocuments(
  units: readonly ResearchUnit[],
  previous: readonly ResearchUnit[] = [],
): SweepDocument[] {
  const before = new Map<string, string>();
  for (const unit of previous) {
    const url = documentUrl(unit);
    if (url && unit.key.contentSha256) before.set(url, unit.key.contentSha256);
  }
  const byUrl = new Map<string, SweepDocument>();
  for (const unit of units) {
    const url = documentUrl(unit);
    if (!url) continue;
    const current = byUrl.get(url) ?? {
      url,
      host: hostOf(url),
      uf: null,
      segments: [],
      sha: null,
      change: "PENDING" as DocumentChange,
      failed: false,
    };
    const uf = unit.key.destinationUf ?? unit.key.uf ?? null;
    const segment = unit.key.segment;
    byUrl.set(url, {
      ...current,
      uf: current.uf ?? uf,
      segments:
        segment && !current.segments.includes(segment)
          ? [...current.segments, segment].sort()
          : current.segments,
      sha: current.sha ?? unit.key.contentSha256 ?? null,
      failed: current.failed || unit.status === "FAILED",
    });
  }
  return [...byUrl.values()]
    .map((document) => {
      if (!document.sha) return document;
      const seen = before.get(document.url);
      const change: DocumentChange = !seen ? "NEW" : seen === document.sha ? "SAME" : "CHANGED";
      return { ...document, change };
    })
    .sort(
      (a, b) =>
        changeRank(a.change) - changeRank(b.change) ||
        (a.uf ?? "~").localeCompare(b.uf ?? "~") ||
        a.host.localeCompare(b.host),
    );
}

function changeRank(change: DocumentChange): number {
  return change === "CHANGED" ? 0 : change === "NEW" ? 1 : change === "PENDING" ? 2 : 3;
}

/** The latest earlier sweep that located documents, for "changed since the last run". */
export function previousSweep(
  sweeps: readonly ResearchSweep[],
  sweep: ResearchSweep,
): ResearchSweep | undefined {
  return sweeps
    .filter(
      (candidate) =>
        candidate.id !== sweep.id &&
        candidate.createdAt < sweep.createdAt &&
        candidate.totals.byKind.FETCH_DOCUMENT + candidate.totals.byKind.EXTRACT_DOCUMENT_SEGMENT >
          0,
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

/** One short line naming what a unit researches. */
export function unitSubject(unit: ResearchUnit): string {
  const key = unit.key;
  const parts: string[] = [];
  if (key.segment) parts.push(`${key.segment}${key.segmentName ? ` ${key.segmentName}` : ""}`);
  if (key.ncm) parts.push(key.ncm.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1.$2.$3"));
  if (key.originUfs?.length) parts.push(`${key.originUfs.join(", ")} →`);
  const uf = key.destinationUf ?? key.uf;
  if (uf) parts.push(uf);
  if (key.canonicalUrl) parts.push(hostOf(key.canonicalUrl));
  return parts.join(" · ") || "—";
}

/* -- Why a sweep ended with failures ---------------------------------------- */

/**
 * Failure reasons grouped by what the operator can do about them. The counts
 * come from the sweep's completion (every failed unit); the affected UFs and
 * hosts come from the failed units actually loaded, so they are named only
 * when the API returned them.
 */
export type FailureCategory =
  | "blockedDownloads"
  | "missingDownloads"
  | "noOfficialSource"
  | "providerLimits"
  | "extraction"
  | "closedByOperator"
  | "other";

export const FAILURE_CATEGORY_ORDER: readonly FailureCategory[] = [
  "blockedDownloads",
  "missingDownloads",
  "noOfficialSource",
  "extraction",
  "providerLimits",
  "closedByOperator",
  "other",
];

export function failureCategory(code: string): FailureCategory {
  const http = /^FETCH_HTTP_(\d{3})$/.exec(code);
  if (http) return Number(http[1]) < 500 ? "blockedDownloads" : "missingDownloads";
  // FETCH_BLOCKED: the host refuses our egress address (allowlisting pending).
  if (code === "URL_NOT_ALLOWED" || code === "FETCH_BLOCKED") return "blockedDownloads";
  if (code.startsWith("FETCH_")) return "missingDownloads";
  if (code === "NO_OFFICIAL_CITATION" || code === "NO_REVIEWED_SOURCE") return "noOfficialSource";
  if (
    code === "PROVIDER_RATE_LIMITED" ||
    code === "PROVIDER_UNAVAILABLE" ||
    code === "LEASE_EXPIRED" ||
    code === "WORKER_ERROR"
  ) {
    return "providerLimits";
  }
  if (
    code.startsWith("EXTRACT") ||
    code === "DOCUMENT_UNSUPPORTED" ||
    code === "CHUNK_TOO_LARGE" ||
    code === "DOCUMENT_CHANGED" ||
    code === "RESULT_INVALID" ||
    code === "GROUNDED_RESPONSE_INVALID"
  ) {
    return "extraction";
  }
  if (code === "CLOSED_BY_OPERATOR") return "closedByOperator";
  return "other";
}

/** The UF of an official state host (`www.sefaz.pa.gov.br` → PA), if it names one. */
export function ufFromHost(host: string): string | null {
  const match = /\.([a-z]{2})\.gov\.br$/i.exec(host);
  const uf = match?.[1]?.toUpperCase();
  return uf && ufSchema.safeParse(uf).success ? uf : null;
}

export type FailureGroup = {
  category: FailureCategory;
  count: number;
  codes: string[];
  /** Destination UFs (or the UF of the host) among the loaded failed units. */
  ufs: string[];
  /** Hosts that name no UF (CONFAZ, Senado), from the loaded failed units. */
  hosts: string[];
};

export function diagnoseFailures(
  reasons: ReadonlyArray<{ code: string; count: number }>,
  units: readonly ResearchUnit[],
): FailureGroup[] {
  const groups = new Map<FailureCategory, FailureGroup>();
  const group = (category: FailureCategory) => {
    const existing = groups.get(category);
    if (existing) return existing;
    const created: FailureGroup = { category, count: 0, codes: [], ufs: [], hosts: [] };
    groups.set(category, created);
    return created;
  };
  for (const reason of reasons) {
    const target = group(failureCategory(reason.code));
    target.count += reason.count;
    if (!target.codes.includes(reason.code)) target.codes.push(reason.code);
  }
  for (const unit of units) {
    const code = unit.lastError?.code;
    if (!code) continue;
    const target = groups.get(failureCategory(code));
    if (!target) continue;
    const host = unit.key.canonicalUrl ? hostOf(unit.key.canonicalUrl) : null;
    const uf = unit.key.destinationUf ?? unit.key.uf ?? (host ? ufFromHost(host) : null);
    if (uf && !target.ufs.includes(uf)) target.ufs.push(uf);
    else if (!uf && host && !target.hosts.includes(host)) target.hosts.push(host);
  }
  return FAILURE_CATEGORY_ORDER.flatMap((category) => {
    const found = groups.get(category);
    return found && found.count > 0
      ? [{ ...found, ufs: [...found.ufs].sort(), hosts: [...found.hosts].sort() }]
      : [];
  });
}
